import React from 'react';
import {useGetTestLists} from '../../../hooks/api/testList';
import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Stack,
  Table,
  Th,
  Tr,
  Thead,
  TableContainer,
  Td,
  Tbody,
  Alert,
  AlertIcon,
  useDisclosure,
  HStack,
  Tooltip,
} from '@chakra-ui/react';
import {pluralize} from '../../../lib/helpers';
import {WithId} from '../../../shared/util';
import {TestList} from '../../../shared/testLists';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import {format} from 'date-fns';
import {EmptyTestListsContainer} from './EmptyTestListsContainer';
import {TestListLabel} from './TestListLabel';
import {OverflowMenuVertical} from '@carbon/icons-react';
import {EnrichAccountsModal} from '../../AccountGrid/header/EnrichButton';
import {useIsFreeOrListBuilderCustomer} from '../../../hooks/api/metadata';
import {DeleteTestListModal} from './DeleteTestListModal';
import {ImportTestListModal} from './ImportTestListModal';
import {MaxTestLists} from '../../../shared/api/definitions/testList';

export const TestLists = () => {
  const {data, isError, isPending} = useGetTestLists({});
  const importTestListModal = useDisclosure();

  return (
    <Flex direction="column" gap="4">
      <HStack justifyContent="space-between">
        <Flex direction="column">
          <Box fontWeight="500">Test Lists</Box>
          <Box textColor="kgray.300" fontSize="sm">
            {pluralize(data?.length ?? 0, 'list', 'lists')}
          </Box>
        </Flex>
        <Tooltip
          label={`Maximum of ${MaxTestLists} lists allowed`}
          isDisabled={(data?.length ?? 0) < MaxTestLists}
          placement="auto"
        >
          <Button
            colorScheme="kbuttonblue"
            fontWeight="normal"
            isDisabled={(data?.length ?? 0) >= MaxTestLists || isPending}
            onClick={importTestListModal.onOpen}
          >
            Import List
          </Button>
        </Tooltip>
        <ImportTestListModal
          isOpen={importTestListModal.isOpen}
          onClose={importTestListModal.onClose}
        />
      </HStack>
      {isPending ? (
        <Stack gap="4">
          <Skeleton height="40px" />
          <Skeleton height="40px" />
          <Skeleton height="40px" />
        </Stack>
      ) : isError ? (
        <Alert status="error">
          <AlertIcon />
          Error loading test lists. Please reload the page or contact us for
          help.
        </Alert>
      ) : !data.length ? (
        <EmptyTestListsContainer />
      ) : (
        <TestListsTable testLists={data} />
      )}
    </Flex>
  );
};

const columnHelper = createColumnHelper<WithId<TestList>>();
const columns = [
  columnHelper.accessor((testList) => testList, {
    header: 'List Name',
    cell: (cell) => <TestListLabel testList={cell.getValue()} />,
  }),
  columnHelper.accessor('status', {
    header: () => <Flex justifyContent="center">Accounts</Flex>,
    cell: (cell) => {
      const status = cell.getValue();
      return (
        <Flex justifyContent="center">
          {status.type === 'processing' ? (
            <Box
              bg="kblue.50"
              borderRadius="lg"
              color="kblue.500"
              fontSize="sm"
              px="2"
              py="1"
              textTransform="uppercase"
            >
              Processing
            </Box>
          ) : (
            status.numAccounts.toLocaleString()
          )}
        </Flex>
      );
    },
  }),
  columnHelper.accessor('timestamp', {
    header: () => <Flex justifyContent="center">Added</Flex>,
    cell: (cell) => {
      const timestamp = cell.getValue();
      return (
        <Flex justifyContent="center">{format(timestamp, 'M/dd/yy')}</Flex>
      );
    },
  }),
  columnHelper.accessor((testList) => testList, {
    id: 'actions',
    header: '',
    size: 30,
    cell: (info) => <TestListMenu testList={info.getValue()} />,
    enableSorting: false,
  }),
];

const TestListMenu = ({testList}: {testList: WithId<TestList>}) => {
  const enrichAccountsModal = useDisclosure();
  const deleteTestListModal = useDisclosure();
  const isFreeOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();

  if (isFreeOrListBuilderCustomer || testList.status.type !== 'ready') {
    return null;
  }

  return (
    <Box textAlign="end">
      <Menu>
        <MenuButton as={Button} variant="ghost" size="sm" p="2">
          <OverflowMenuVertical />
        </MenuButton>
        <MenuList>
          <MenuItem onClick={enrichAccountsModal.onOpen}>
            Enrich Accounts
            <EnrichAccountsModal
              accountSelection={{
                name: 'testList',
                listId: testList._id,
              }}
              isOpen={enrichAccountsModal.isOpen}
              onClose={enrichAccountsModal.onClose}
              modalTitle={`Enrich Test List Accounts: ${testList.label}`}
            />
          </MenuItem>
          {(testList.type === 'uploaded' || testList.type === 'samSample') && (
            <MenuItem color="kred.500" onClick={deleteTestListModal.onOpen}>
              Delete Test List
              <DeleteTestListModal
                testList={testList}
                isOpen={deleteTestListModal.isOpen}
                onClose={deleteTestListModal.onClose}
              />
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </Box>
  );
};

const TestListsTable = ({testLists}: {testLists: WithId<TestList>[]}) => {
  const table = useReactTable({
    columns,
    data: testLists,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row._id.toString(),
    defaultColumn: {
      minSize: 0,
      size: 150,
    },
  });

  return (
    <TableContainer border="1px" borderColor="kgray.200" borderRadius="md">
      <Table variant="simple">
        <Thead bg="kgray.100">
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  w={header.getSize() ? `${header.getSize()}px` : 'auto'}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id} role="group" height="70px">
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={cell.id}
                  _groupHover={{bg: 'kgray.100'}}
                  w={
                    cell.getContext().column.getSize()
                      ? `${cell.getContext().column.getSize()}px`
                      : 'auto'
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};
